/**
 * Per-board process-card field config (field pack v1) — the frontend
 * resolver for `tenants.config.workspace.process_card_fields`, keyed by
 * the process's immutable KEY (labels rename freely; keys never do, so a
 * board rename can't break the binding). Sibling of readTaskCategories
 * (same defensive-walk discipline; delivered by /getTenantConfig).
 *
 * Shape per process key:
 *   { category?: { label: string, options: string[], multi?: boolean },
 *     due_date_label?: string,
 *     folder_link?: { label: string, url: string } }
 *
 * `category` drives the board's categorical card field (its display
 * label, e.g. a hiring board's "Role", is tenant DATA — never a
 * hardcoded string), the header filter, the per-lane sort option, and
 * the card dot. `due_date_label` re-labels the card date field (e.g.
 * "Application date"). Absent/malformed config → nulls → the board
 * renders exactly as before (intake and unconfigured boards untouched).
 *
 * Values are operator guidance, not a vocabulary lock: the server keeps
 * process-item category free text, and pickers keep off-list stored
 * values selectable. Setting config is operator data work via a
 * tools/recon/ trail.
 */

export interface ProcessCardCategoryField {
  label: string;
  options: readonly string[];
  /** true = a card may carry several values (a candidate for two roles):
   *  the drawer offers a checkbox group writing `categories`, the card
   *  shows chips. false (the default) = one value, the select. */
  multi: boolean;
}

/** A board-level folder link (e.g. a hiring board's talent folder): the
 *  card and drawer render it under this label when the card carries no
 *  per-card allows_link URL of its own. */
export interface ProcessCardFolderLink {
  label: string;
  url: string;
}

export interface ProcessCardFields {
  category: ProcessCardCategoryField | null;
  due_date_label: string | null;
  folder_link: ProcessCardFolderLink | null;
}

export const EMPTY_PROCESS_CARD_FIELDS: ProcessCardFields = { category: null, due_date_label: null, folder_link: null };
const EMPTY = EMPTY_PROCESS_CARD_FIELDS;

/** A folder link needs a non-blank label and an http(s) URL (it renders as
 *  an <a href>, so no other scheme is ever accepted from config). */
function readFolderLink(raw: unknown): ProcessCardFolderLink | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const label = (raw as Record<string, unknown>).label;
  const url = (raw as Record<string, unknown>).url;
  if (typeof label !== 'string' || label.trim().length === 0) return null;
  if (typeof url !== 'string') return null;
  const trimmedUrl = url.trim();
  if (!/^https?:\/\/\S+$/i.test(trimmedUrl)) return null;
  return { label: label.trim(), url: trimmedUrl };
}

/** Defensive walk config.workspace.process_card_fields[processKey]. Pure;
 *  unit-tested (tenantConfigSeams). Never throws on malformed config. */
export function readProcessCardFields(config: unknown, processKey: string): ProcessCardFields {
  if (typeof config !== 'object' || config === null) return EMPTY;
  const workspace = (config as Record<string, unknown>).workspace;
  if (typeof workspace !== 'object' || workspace === null) return EMPTY;
  const all = (workspace as Record<string, unknown>).process_card_fields;
  if (typeof all !== 'object' || all === null) return EMPTY;
  const entry = (all as Record<string, unknown>)[processKey];
  if (typeof entry !== 'object' || entry === null) return EMPTY;

  const rawCategory = (entry as Record<string, unknown>).category;
  let category: ProcessCardCategoryField | null = null;
  if (typeof rawCategory === 'object' && rawCategory !== null) {
    const label = (rawCategory as Record<string, unknown>).label;
    const rawOptions = (rawCategory as Record<string, unknown>).options;
    if (typeof label === 'string' && label.trim().length > 0) {
      const options = Array.isArray(rawOptions)
        ? rawOptions
            .filter((o): o is string => typeof o === 'string' && o.trim().length > 0)
            .map((o) => o.trim())
        : [];
      const multi = (rawCategory as Record<string, unknown>).multi === true;
      category = { label: label.trim(), options, multi };
    }
  }

  const rawDueDateLabel = (entry as Record<string, unknown>).due_date_label;
  const due_date_label =
    typeof rawDueDateLabel === 'string' && rawDueDateLabel.trim().length > 0
      ? rawDueDateLabel.trim()
      : null;

  const folder_link = readFolderLink((entry as Record<string, unknown>).folder_link);

  return category === null && due_date_label === null && folder_link === null
    ? EMPTY
    : { category, due_date_label, folder_link };
}
